import { supabase } from '@/lib/supabase';
import { ServiceResult, createError } from './types';

export interface Cleaning {
  id: string;
  property_id: string;
  started_at: string;
  completed_at: string | null;
  status: 'in_progress' | 'completed';
}

export async function startCleaning(
  propertyId: string
): Promise<ServiceResult<{ id: string }>> {
  const { data, error } = await supabase
    .from('cleanings')
    .insert([{ property_id: propertyId }])
    .select('id')
    .single();

  if (error) {
    return {
      data: null,
      error: createError('Failed to start cleaning session', error.message, error.code),
    };
  }

  return { data, error: null };
}

export async function completeCleaning(
  cleaningId: string
): Promise<ServiceResult<null>> {
  const { error } = await supabase
    .from('cleanings')
    .update({
      status: 'completed',
      completed_at: new Date().toISOString(),
    })
    .eq('id', cleaningId);

  if (error) {
    return {
      data: null,
      error: createError('Failed to complete cleaning session', error.message, error.code),
    };
  }

  return { data: null, error: null };
}

export async function getActiveCleaning(
  propertyId: string
): Promise<ServiceResult<Cleaning | null>> {
  const { data, error } = await supabase
    .from('cleanings')
    .select('*')
    .eq('property_id', propertyId)
    .eq('status', 'in_progress')
    .maybeSingle();

  if (error) {
    return {
      data: null,
      error: createError('Failed to check for active cleaning', error.message, error.code),
    };
  }

  return { data, error: null };
}

export async function recordItemCompletion(
  cleaningId: string,
  itemId: string
): Promise<ServiceResult<null>> {
  const { error } = await supabase
    .from('cleaning_item_completions')
    .upsert([{ cleaning_id: cleaningId, item_id: itemId }], {
      onConflict: 'cleaning_id,item_id',
    });

  if (error) {
    return {
      data: null,
      error: createError('Failed to save completion', error.message, error.code),
    };
  }

  return { data: null, error: null };
}

export async function removeItemCompletion(
  cleaningId: string,
  itemId: string
): Promise<ServiceResult<null>> {
  const { error } = await supabase
    .from('cleaning_item_completions')
    .delete()
    .eq('cleaning_id', cleaningId)
    .eq('item_id', itemId);

  if (error) {
    return {
      data: null,
      error: createError('Failed to remove completion', error.message, error.code),
    };
  }

  return { data: null, error: null };
}

export async function getCompletedItems(
  cleaningId: string
): Promise<ServiceResult<string[]>> {
  const { data, error } = await supabase
    .from('cleaning_item_completions')
    .select('item_id')
    .eq('cleaning_id', cleaningId);

  if (error) {
    return {
      data: null,
      error: createError('Failed to load completed items', error.message, error.code),
    };
  }

  return { data: data.map((row) => row.item_id), error: null };
}

export async function toggleItemCompletion(
  cleaningId: string,
  itemId: string,
  currentlyCompleted: boolean
): Promise<ServiceResult<null>> {
  if (currentlyCompleted) {
    return removeItemCompletion(cleaningId, itemId);
  } else {
    return recordItemCompletion(cleaningId, itemId);
  }
}


